import { useEffect, useState } from "react";
import { invoke } from "@tauri-apps/api/core";
import { isTauri } from "@/lib/is-tauri";
import type { BrowserId } from "./browser-connection";

const found = new Map<BrowserId, string>();
const pending = new Map<BrowserId, Promise<string | null>>();

/** The browser's own app icon, read natively from the desktop's catalog
 * whether it runs or not. Only found icons are kept: a browser installed
 * later is read again. */
export function resolveBrowserIcon(
  browserId: BrowserId,
): Promise<string | null> {
  const icon = found.get(browserId);
  if (icon) return Promise.resolve(icon);
  if (!isTauri()) return Promise.resolve(null);
  const reading = pending.get(browserId);
  if (reading) return reading;
  const read = invoke<string>("get_browser_icon", { browserId })
    .then((url) => {
      found.set(browserId, url);
      return url;
    })
    .catch(() => null)
    .finally(() => pending.delete(browserId));
  pending.set(browserId, read);
  return read;
}

/** A found icon paints at once, so a toast expanding or collapsing never
 * flashes the fallback. */
export function useBrowserIcon(browserId: BrowserId): string | null {
  const [icon, setIcon] = useState(() => ({
    browserId,
    url: found.get(browserId) ?? null,
  }));
  useEffect(() => {
    let current = true;
    void resolveBrowserIcon(browserId).then((url) => {
      if (current) setIcon({ browserId, url });
    });
    return () => {
      current = false;
    };
  }, [browserId]);
  return icon.browserId === browserId
    ? icon.url
    : (found.get(browserId) ?? null);
}
